// Thin wrapper around the browser's native Notification API — fires whenever
// EZBuild is open in any tab (active or backgrounded), even unfocused. It
// does NOT fire if the browser/tab is fully closed; that would need a real
// Web Push subsystem (service worker + push subscription + VAPID keys),
// which this app does not have for web today (only FCM for the mobile app).

export type DesktopNotificationPermission =
  | "granted"
  | "denied"
  | "default"
  | "unsupported"

export const isDesktopNotificationSupported = (): boolean =>
  typeof window !== "undefined" && "Notification" in window

export const getDesktopNotificationPermission =
  (): DesktopNotificationPermission => {
    if (!isDesktopNotificationSupported()) return "unsupported"
    return Notification.permission
  }

// Never re-prompts once the user has answered — `Notification.requestPermission`
// itself is a no-op returning the existing value for "granted"/"denied", but
// this guard keeps every call site from needing to know that.
export const requestDesktopNotificationPermission =
  async (): Promise<DesktopNotificationPermission> => {
    if (!isDesktopNotificationSupported()) return "unsupported"
    if (Notification.permission !== "default") return Notification.permission
    return Notification.requestPermission()
  }

interface ShowDesktopNotificationOptions {
  title: string
  body?: string
  icon?: string
  // The service worker tags its Web Push alert `notification-<id>`; the same
  // tag here makes the browser replace rather than stack the second copy.
  tag?: string
  onClick?: () => void
}

// Silently does nothing without permission — the caller's own in-app
// notification/toast is what carries the message in that case, per the
// "never block on a denied desktop permission" rule.
export const showDesktopNotification = ({
  title,
  body,
  icon,
  tag,
  onClick
}: ShowDesktopNotificationOptions): Notification | null => {
  if (getDesktopNotificationPermission() !== "granted") return null

  const notification = new Notification(title, { body, icon, tag })
  if (onClick) {
    notification.onclick = () => {
      window.focus()
      onClick()
      notification.close()
    }
  }
  return notification
}
